import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Leaf, Menu, X, Heart, LogOut, Users, Check, ChevronDown } from 'lucide-react';
import { useKit } from '../hooks/useKit';
import { useAuth } from '../contexts/AuthContext';
import { useProfiles } from '../contexts/ProfileContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from './ui/dropdown-menu';

const links = [
  { to: '/', label: 'Home' },
  { to: '/library', label: 'Library' },
  { to: '/recipes', label: 'Recipes' },
  { to: '/kit', label: 'First Aid Kit' },
  { to: '/favorites', label: 'Favorites' },
  { to: '/shopping-list', label: 'Shopping List' },
  { to: '/ask', label: 'Ask Sage' },
  { to: '/membership', label: 'Membership' }
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { items } = useKit();
  const { user, openAuth, logout } = useAuth();
  const { profiles, activeProfile, setActive } = useProfiles();

  const isActive = (to) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50 bg-botanica-panel/85 backdrop-blur-md border-b border-botanica-card">
      <nav className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <span className="w-9 h-9 rounded-xl bg-botanica-greenLight flex items-center justify-center transition-transform group-hover:-rotate-6">
            <Leaf className="w-5 h-5 text-botanica-green" />
          </span>
          <span className="font-serif text-lg md:text-xl font-semibold text-botanica-ink tracking-tight leading-none">Your Herbal Companion</span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                isActive(l.to)
                  ? 'bg-botanica-greenLight text-botanica-greenDark font-medium'
                  : 'text-botanica-muted hover:text-botanica-ink'
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/favorites" className="relative text-botanica-muted hover:text-botanica-green transition-colors" title="Favorites">
            <Heart className="w-5 h-5" />
            {items.length > 0 && (
              <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-botanica-green text-white text-[10px] flex items-center justify-center">
                {items.length}
              </span>
            )}
          </Link>
          {user ? (
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-botanica-card/60 hover:bg-botanica-card text-sm text-botanica-ink transition-colors focus:outline-none">
                  <Users className="w-4 h-4 text-botanica-green" />
                  <span className="max-w-[90px] truncate">{activeProfile ? activeProfile.first_name : 'Profiles'}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-botanica-muted" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-botanica-panel border-botanica-card">
                  <DropdownMenuLabel className="text-botanica-muted text-xs">Viewing as</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setActive(null)} className="cursor-pointer">
                    <span className="flex-1">Everyone (no filter)</span>
                    {!activeProfile && <Check className="w-4 h-4 text-botanica-green" />}
                  </DropdownMenuItem>
                  {profiles.map((p) => (
                    <DropdownMenuItem key={p.profile_id} onClick={() => setActive(p.profile_id)} className="cursor-pointer">
                      <span className="flex-1 truncate">{p.first_name}{p.is_family ? ' (family)' : ''}</span>
                      {activeProfile?.profile_id === p.profile_id && <Check className="w-4 h-4 text-botanica-green" />}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer text-botanica-green">
                    Manage profiles
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover border border-botanica-card" />
              ) : (
                <span className="w-8 h-8 rounded-full bg-botanica-greenLight text-botanica-greenDark text-sm flex items-center justify-center font-medium">
                  {user.name?.[0]?.toUpperCase() || 'B'}
                </span>
              )}
              <span className="text-sm text-botanica-ink max-w-[90px] truncate">{user.name}</span>
              <button onClick={logout} className="text-botanica-muted hover:text-botanica-ink transition-colors" title="Sign out">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <button onClick={() => openAuth('login')} className="text-sm text-botanica-muted hover:text-botanica-ink transition-colors">
                Sign in
              </button>
              <button
                onClick={() => openAuth('register')}
                className="text-sm px-4 py-2 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors"
              >
                Get started
              </button>
            </>
          )}
        </div>

        <button className="lg:hidden text-botanica-ink" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-botanica-card bg-botanica-panel px-5 py-3 animate-soft-in">
          <div className="flex flex-col">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={`py-2.5 text-sm ${isActive(l.to) ? 'text-botanica-greenDark font-medium' : 'text-botanica-muted'}`}
              >
                {l.label}
              </Link>
            ))}
            {user && (
              <Link to="/profile" onClick={() => setOpen(false)} className="py-2.5 text-sm text-botanica-muted flex items-center gap-2">
                <Users className="w-4 h-4" /> Profiles{activeProfile ? ` · ${activeProfile.first_name}` : ''}
              </Link>
            )}
            <button
              onClick={() => { setOpen(false); user ? logout() : openAuth('register'); }}
              className="mt-3 text-sm px-4 py-2.5 rounded-full bg-botanica-green text-white font-medium"
            >
              {user ? 'Sign out' : 'Get started'}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
